/**
 * @fileoverview Shared types and metadata for the Day-One generated
 * documents data table.
 *
 * Kept separate from `columns.tsx` so the page module can import the
 * row type and metadata without pulling in TanStack column defs.
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { JobStatus } from "@cvx/jobs/schema";
import type { ContactRole, FirmContactCategory } from "@/lib/contact-roles";

/**
 * Send-status union mirroring the Convex `sendStatusValidator` enum.
 * Flipped by the mail pipeline when a Day One draft is approved/sent
 * (`markPrecedentSent`); the Actions column shows "Sent via email".
 */
export type SendStatus = "unsent" | "sent" | "response_received";

/**
 * Attachment fields the table needs to nest generated-precedent siblings
 * under their parent pack. Mirrors the resolved `attachments` overlay
 * from `listByJob` without pulling in download URLs.
 */
export interface DayOneListAttachment {
  sourceKind: string;
  status?: string;
  generatedPrecedentId?: Id<"jobPrecedents">;
  generatedTemplateKey?: string;
  /** Slot label from the template (e.g. "Court Order"). */
  name?: string;
  /** Resolved file name for uploaded / job-document attachments. */
  fileName?: string;
  fileType?: string;
  /** Storage URL for uploaded / job-document attachments. */
  downloadUrl?: string | null;
}

/**
 * An uploaded / job-document attachment rendered as its own nested row
 * (MOR-971). Unlike generated-precedent attachments it has no
 * `jobPrecedents` row of its own, so it can't be edited or deleted here;
 * it previews and downloads straight from storage.
 */
export interface DayOneFileAttachment {
  /** Slot label from the template (e.g. "Court Order"). */
  slotName: string;
  fileName?: string;
  fileType?: string;
  downloadUrl: string | null;
  status: string;
}

/**
 * The subset of a `listByJob` row that the table projector reads.
 * Wider than `DayOnePrecedentRow` because nesting needs the attachment
 * links; narrower than the raw Convex doc (no `editedHtml`, padding, …).
 */
export interface DayOneListItem {
  _id: Id<"jobPrecedents">;
  templateKey: string;
  name: string;
  category: string;
  isThirdPartyForm?: boolean;
  sendStatus: SendStatus;
  workflowStatusId?: Id<"taskStatuses">;
  /** Phase (job-status) tags derived live from the template's `js:` tags. */
  phaseTags?: JobStatus[];
  jobContactId?: Id<"jobContacts">;
  /** listByJob returns this as a plain string; narrowed when projecting. */
  contactRoleKind?: string;
  firmContactId?: Id<"firmContacts">;
  firmContactCategory?: string;
  contactDisplayName?: string | null;
  contactExists?: boolean;
  attachments?: DayOneListAttachment[];
}

/**
 * Slim row shape consumed by the data table. Mirrors the union of
 * `jobPrecedents` schema fields plus the `contactDisplayName` /
 * `contactExists` overlay added by `convex/jobs/precedents/queries:listByJob`.
 *
 * Intentionally narrower than the raw Convex doc — extra fields like
 * `editedHtml`, padding overrides, etc. are needed by the
 * preview/download flow but not the table itself, so we leave those on
 * the parent's full row type and only surface what the table renders
 * or sorts on.
 */
export interface DayOnePrecedentRow {
  _id: Id<"jobPrecedents">;
  /**
   * Unique TanStack row id. Parents use `_id`; a document nested under
   * several packs uses `${parentTableRowId}::${_id}` so the same
   * precedent can appear twice without colliding.
   */
  tableRowId: string;
  templateKey: string;
  name: string;
  category: string;
  isThirdPartyForm?: boolean;
  sendStatus: SendStatus;
  /** Firm workflow status (org taskStatuses) — MOR-736. */
  workflowStatusId?: Id<"taskStatuses">;
  /** Phase (job-status) tags derived live from the template's `js:` tags. */
  phaseTags?: JobStatus[];
  jobContactId?: Id<"jobContacts">;
  contactRoleKind?: ContactRole;
  firmContactId?: Id<"firmContacts">;
  firmContactCategory?: FirmContactCategory;
  contactDisplayName?: string | null;
  contactExists?: boolean;
  /** Nested under a pack that attaches this generated document. */
  isAttachment: boolean;
  /** Cover-letter name, set on nested attachment rows. */
  attachedToName?: string;
  parentPrecedentId?: Id<"jobPrecedents">;
  /**
   * Set on nested rows that represent an uploaded / job-document file
   * rather than a generated document. `_id` on such rows is the parent
   * pack's id (there is no precedent of their own).
   */
  fileAttachment?: DayOneFileAttachment;
  /** Non-skipped attachment slots on this pack (generated + files). */
  attachmentCount: number;
  /** Attachments nested under this pack (generated documents and files). */
  subRows?: DayOnePrecedentRow[];
}

/**
 * Helper: project a raw Convex `jobPrecedents` row (with the extra
 * `contactDisplayName` / `contactExists` overlay) down to the
 * `DayOnePrecedentRow` shape used by the table. Nesting / pack
 * hierarchy is applied separately by `nestGeneratedAttachments`.
 */
export function projectDayOneRow(doc: DayOneListItem): DayOnePrecedentRow {
  return {
    _id: doc._id,
    tableRowId: doc._id,
    templateKey: doc.templateKey,
    name: doc.name,
    category: doc.category,
    isThirdPartyForm: doc.isThirdPartyForm,
    sendStatus: doc.sendStatus,
    workflowStatusId: doc.workflowStatusId,
    phaseTags: doc.phaseTags,
    jobContactId: doc.jobContactId,
    contactRoleKind: doc.contactRoleKind as ContactRole | undefined,
    firmContactId: doc.firmContactId,
    firmContactCategory: doc.firmContactCategory as
      FirmContactCategory | undefined,
    contactDisplayName: doc.contactDisplayName,
    contactExists: doc.contactExists,
    isAttachment: false,
    attachmentCount: 0,
  };
}
